import type { IMdBlock } from './IMdBlock';
import type { IMdInline } from './IMdInline';
import { mdInline } from './mdInline';

const FENCE = '```';
/** Клітинки рядка таблиці: роздільник «|» поза дужками {колір|текст}; «\|» — символ у клітинці. */
const cells = (line: string): string[] => {
  const out: string[] = [];
  let cur = '';
  let depth = 0;
  const body = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i]!;
    if (ch === '\\' && body[i + 1] === '|') { cur += '|'; i += 1; continue; }
    if (ch === '{') depth += 1;
    if (ch === '}') depth = Math.max(0, depth - 1);
    if (ch === '|' && depth === 0) { out.push(cur.trim()); cur = ''; } else cur += ch;
  }
  out.push(cur.trim());
  return out;
};

/**
 * Блоки Markdown статті (прототип mdToHtml): ``` код ``` і ```query (живий запит), таблиці | … |, заголовки # – ####, списки - * •
 * та 1. 1), цитати >, лінія --- / ***, абзаци (порожній рядок закриває список). Чиста функція.
 */
export function mdBlocks(text: string): IMdBlock[] {
  const out: IMdBlock[] = [];
  const lines = String(text ?? '').split('\n');
  let items: IMdInline[][] | null = null;
  let listKind = '';
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!.replace(/\s+$/, '');
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (ul || ol) {
      const kind = ul ? 'ul' : 'ol';
      if (!items || listKind !== kind) { items = []; listKind = kind; out.push({ kind, items }); }
      items.push(mdInline((ul ?? ol)![1]!));
      continue;
    }
    items = null;
    if (line.trim().startsWith(FENCE)) {
      const lang = line.trim().slice(3).trim().toLowerCase();
      const body: string[] = [];
      for (i += 1; i < lines.length && !lines[i]!.trim().startsWith(FENCE); i += 1) body.push(lines[i]!);
      out.push(lang === 'query' ? { kind: 'query', lines: body } : { kind: 'code', text: body.join('\n') });
    } else if (/^\s*\|/.test(line)) {
      const rows: string[][] = [];
      for (; i < lines.length && /^\s*\|/.test(lines[i]!); i += 1) {
        const r = cells(lines[i]!);
        if (!r.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(r);
      }
      i -= 1;
      const head = rows.shift() ?? [];
      out.push({ kind: 'table', head: head.map(mdInline), rows: rows.map((r) => head.map((_, k) => mdInline(r[k] ?? ''))) });
    } else {
      const h = /^(#{1,4})\s+(.*)$/.exec(line);
      const q = /^>\s?(.*)$/.exec(line);
      if (h) out.push({ kind: 'heading', level: h[1]!.length, inlines: mdInline(h[2]!) });
      else if (q) out.push({ kind: 'quote', inlines: mdInline(q[1]!) });
      else if (/^\s*(---|\*\*\*)\s*$/.test(line)) out.push({ kind: 'hr' });
      else if (line.trim()) out.push({ kind: 'paragraph', inlines: mdInline(line) });
    }
  }
  return out;
}
